import React from "react";
import { globalSocketService, SESSION_ID } from "./globalSocketService";
import { browserNotificationService } from "./browserNotificationService";
import moment from "moment";
import { toast } from "react-toastify";

/**
 * plansSocketService — V4
 *
 * Cambios clave respecto a V3 para arreglar el problema crónico de "el
 * usuario no se entera cuando termina la planificación con IA":
 *
 * 1. Watcher unificado de planificación inteligente que combina **3 canales**
 *    funcionando en paralelo y deduplicados:
 *      a) WebSocket (push instantáneo).
 *      b) Polling REST cada 30 s a `getPlanIntelligentStatus`, ejecutado
 *         desde un Web Worker propio para evitar el throttling de
 *         setInterval en pestañas en background (Chrome lo throttea a
 *         1 ejecución/min en bg, lo que retrasa el aviso varios minutos).
 *      c) Eventos `tab-visible` y `reconnected` que disparan un check
 *         REST inmediato (cubre microcaídas del WS y vuelta de pestaña).
 *
 * 2. Notificación nativa del navegador (vía browserNotificationService)
 *    cuando el plan termina con la pestaña en background. Como la
 *    planificación puede durar 1 min o 12 horas, el usuario no puede
 *    estar pegado a la pestaña.
 *
 * 3. Deduplicación por (id, state, updated): si llega FINISHED por WS y
 *    por polling, el callback solo se ejecuta una vez.
 *
 * 4. El polling se mantiene hasta que el watcher recibe FINISHED/ERROR.
 *    No depende de que el WS funcione.
 *
 * Importante: después de FINISHED/ERROR el watcher **para** el polling.
 * Al encolar una nueva planificación sin desmontar Plan, hay que llamar
 * a `reviveIntelligentWatchAfterQueuedJob` desde la UI o el primer
 * mensaje PROCESSING lo reactiva desde `_onIntelligentMessage`.
 */

export const plansSocketService = {
	connect,
	disconnect,
	sendMessage,
	isConnected,
	subscribeToIntelligentPlanning,
	reviveIntelligentWatchAfterQueuedJob,
	unsubscribeIntelligentPlanning,
	getConnectionStatus,
	checkIntelligentPlanningStatus,
};

// ─── State del servicio (planes en colaboración) ────────────────────────────

let currentStart = null;
let currentEnd = null;
let currentUser = null;
let currentGroupId = null;

let plansSubscription = null;
let planEditedSubscription = null;

let connectionStatus = "DISCONNECTED";
let onConnectionStatusChange = null;

// Listener de reconexión registrado una sola vez por sesión.
let unsubReconnect = null;

// ─── Estado del watcher de planificación inteligente ────────────────────────

const POLL_INTERVAL_MS = 30_000;
// Frecuencia más alta cuando el WS está caído por mucho tiempo.
const FAST_POLL_INTERVAL_MS = 10_000;
// Tiempo de retén para FINISHED de REST en modo iterativo (semana a semana).
// Si en este tiempo llega un PROCESSING nuevo, se cancela el retén y nunca
// se muestra "Ver resultados" ni se resetea el cronómetro.
const REST_FINISHED_HOLD_MS = 20_000;

let intelligentSubscription = null;
let intelligentCallback = null;
let intelligentGroupId = null;

let pollingWorker = null;
let pollingActive = false;
let degradedMode = false; // true cuando hay muchos fallos de WS seguidos

let lastSeenKey = null; // dedupe (state + id + updated)
let isFinalState = false; // marca si ya llegamos a FINISHED/ERROR
let inFlightCheck = false; // evita pisar peticiones REST simultáneas

let unsubTabVisible = null;
let unsubReconnected = null;
let unsubMaxReconnects = null;

// ─── Estado para el hold de FINISHED en modo iterativo ──────────────────────
// Cuando iterativePlanningMode=true, los FINISHED que vienen del REST polling
// se retienen hasta REST_FINISHED_HOLD_MS. Si en ese tiempo llega un PROCESSING
// (la siguiente semana comenzó) se cancela el retén. El WS nunca se retiene
// (el backend solo manda FINISHED por WS al final de todas las semanas).
let iterativePlanningMode = false;
let restFinishedHoldTimer = null;
let heldFinishedData = null;
let heldFinishedKey = null;

// ─── Web Worker timer para polling no throttable ────────────────────────────
// setInterval en main thread se throttea a 1/min en pestañas background.
// Un Worker dedicado mantiene la cadencia real siempre.

function _createPollingWorker(intervalMs) {
	const code = [
		"let tid = null;",
		"self.onmessage = function(e) {",
		"  if (e.data.command === 'start') {",
		"    if (tid) clearInterval(tid);",
		"    tid = setInterval(function() { self.postMessage('tick'); }, e.data.interval || 30000);",
		"  } else if (e.data.command === 'setInterval') {",
		"    if (tid) clearInterval(tid);",
		"    tid = setInterval(function() { self.postMessage('tick'); }, e.data.interval || 30000);",
		"  } else if (e.data.command === 'stop') {",
		"    if (tid) { clearInterval(tid); tid = null; }",
		"  }",
		"};",
	].join("\n");
	const blob = new Blob([code], { type: "application/javascript" });
	const w = new Worker(URL.createObjectURL(blob));
	w.postMessage({ command: "start", interval: intervalMs });
	return w;
}

function _startPollingWorker() {
	if (pollingWorker) return;
	try {
		pollingWorker = _createPollingWorker(POLL_INTERVAL_MS);
		pollingWorker.onmessage = () => {
			void _runStatusCheck("worker-tick");
		};
	} catch (e) {
		// Si el navegador no permite Workers, hacemos fallback a setInterval
		// (con la limitación conocida en background tabs).
		console.warn(
			`[Plans][WS:${SESSION_ID}] Web Worker no disponible — fallback a setInterval`,
			e
		);
		const id = setInterval(
			() => void _runStatusCheck("interval-tick"),
			POLL_INTERVAL_MS
		);
		pollingWorker = { _fallbackId: id, terminate: () => clearInterval(id), postMessage: () => {} };
	}
}

function _stopPollingWorker() {
	if (!pollingWorker) return;
	try {
		pollingWorker.postMessage({ command: "stop" });
	} catch (_) { /* fallback no soporta postMessage */ }
	try {
		pollingWorker.terminate();
	} catch (_) { /* noop */ }
	pollingWorker = null;
}

function _setPollingInterval(intervalMs) {
	if (!pollingWorker) return;
	if (pollingWorker._fallbackId !== undefined) {
		try {
			clearInterval(pollingWorker._fallbackId);
		} catch (_) {
			/* noop */
		}
		const id = setInterval(
			() => void _runStatusCheck("interval-tick"),
			intervalMs
		);
		pollingWorker._fallbackId = id;
		pollingWorker.terminate = () => clearInterval(id);
		pollingWorker.postMessage = () => {};
		return;
	}
	try {
		pollingWorker.postMessage({
			command: "setInterval",
			interval: intervalMs,
		});
	} catch (_) {
		/* noop */
	}
}

// ─── Listener global de reconexión (para los topics de plans) ───────────────

function _ensureReconnectListener() {
	if (unsubReconnect) return;
	unsubReconnect = globalSocketService.on("reconnected", async () => {
		if (!currentGroupId) return;
		console.log(
			`[Plans][WS:${SESSION_ID}] Reconnected — verificando suscripciones (group ${currentGroupId})`
		);

		const activeTopics = new Set(globalSocketService.getActiveSubscriptions());

		const plansTopic = `/topic/plans/${currentGroupId}`;
		if (plansSubscription && !activeTopics.has(plansTopic)) {
			try {
				plansSubscription = await globalSocketService.subscribe(
					plansTopic,
					(openPlans) => _showMessageOutput(openPlans)
				);
			} catch (e) {
				console.error(
					`[Plans][WS:${SESSION_ID}] Failed to re-subscribe ${plansTopic}:`,
					e
				);
			}
		}

		const planEditedTopic = `/topic/plan-edited/${currentGroupId}`;
		if (planEditedSubscription && !activeTopics.has(planEditedTopic)) {
			try {
				planEditedSubscription = await globalSocketService.subscribe(
					planEditedTopic,
					(openPlan) => _showMessageEdited(openPlan)
				);
			} catch (e) {
				console.error(
					`[Plans][WS:${SESSION_ID}] Failed to re-subscribe ${planEditedTopic}:`,
					e
				);
			}
		}

		const intelligentTopic = `/topic/plans/intelligent-planning/${intelligentGroupId || currentGroupId}`;
		if (intelligentCallback && !activeTopics.has(intelligentTopic)) {
			try {
				intelligentSubscription = await globalSocketService.subscribe(
					intelligentTopic,
					(data) => _onIntelligentMessage(data, "ws")
				);
			} catch (e) {
				console.error(
					`[Plans][WS:${SESSION_ID}] Failed to re-subscribe ${intelligentTopic}:`,
					e
				);
			}
		}

		// Salir del modo degradado: el WS volvió.
		if (degradedMode) {
			degradedMode = false;
			_setPollingInterval(POLL_INTERVAL_MS);
			console.log(
				`[Plans][WS:${SESSION_ID}] WS recuperado — polling vuelve a 30 s`
			);
		}

		// Check inmediato por si nos perdimos un mensaje durante la caída.
		if (intelligentCallback) {
			void _runStatusCheck("reconnected");
		}
	});
}

// ─── API pública ────────────────────────────────────────────────────────────

function isConnected() {
	return globalSocketService.isConnected();
}

function getConnectionStatus() {
	if (isConnected()) return "CONNECTED";
	return connectionStatus;
}

function setConnectionStatus(status) {
	connectionStatus = status;
	if (onConnectionStatusChange) {
		onConnectionStatusChange(status);
	}
}

/**
 * Suscribir a los topics de planificación colaborativa de un grupo.
 * Reusa el WebSocket global; nunca abre uno propio.
 */
async function connect(options) {
	const { group_id, hasPrivileges, start, end, user, onStatusChange } = options;

	currentStart = start;
	currentEnd = end;
	currentUser = user;
	currentGroupId = group_id;
	onConnectionStatusChange = onStatusChange;

	_ensureReconnectListener();
	setConnectionStatus("CONNECTING");

	try {
		if (!globalSocketService.isConnected()) {
			await globalSocketService.connect();
		}

		setConnectionStatus("CONNECTED");

		const plansTopic = `/topic/plans/${group_id}`;
		const planEditedTopic = `/topic/plan-edited/${group_id}`;

		plansSubscription = await globalSocketService.subscribe(
			plansTopic,
			(openPlans) => {
				if (hasPrivileges) {
					_showMessageOutput(openPlans);
				}
			}
		);

		planEditedSubscription = await globalSocketService.subscribe(
			planEditedTopic,
			(openPlan) => _showMessageEdited(openPlan)
		);

		sendMessage({ group_id, start, end });
	} catch (error) {
		console.error(`[Plans][WS:${SESSION_ID}] Error subscribing:`, error);
		setConnectionStatus("ERROR");
	}
}

/**
 * Desuscribir de todos los topics de planes y detener el watcher de IA.
 * NO cierra el WebSocket global (lo siguen usando notificaciones, etc.).
 */
function disconnect() {
	if (plansSubscription) {
		globalSocketService.unsubscribe(`/topic/plans/${currentGroupId}`);
		plansSubscription = null;
	}

	if (planEditedSubscription) {
		globalSocketService.unsubscribe(`/topic/plan-edited/${currentGroupId}`);
		planEditedSubscription = null;
	}

	_stopIntelligentWatcher();

	currentStart = null;
	currentEnd = null;
	currentUser = null;
	currentGroupId = null;
	onConnectionStatusChange = null;

	setConnectionStatus("DISCONNECTED");
}

function sendMessage(options) {
	const { group_id, start, end } = options;

	if (!isConnected()) {
		console.error("Cannot send message: WebSocket not connected");
		return;
	}

	const client = globalSocketService.getClient();
	if (!client) return;

	client.publish({
		destination: `/plain/openplan/${group_id}`,
		headers: {},
		body: JSON.stringify({ start, end, group_id }),
	});
}

// ─── Watcher de planificación inteligente ───────────────────────────────────

/**
 * Empieza a vigilar el estado de la planificación inteligente para un grupo.
 * Combina WS + polling REST (vía Worker) + tab-visible + reconnected.
 *
 * El callback se invoca con el último estado conocido cada vez que cambia.
 * Se ejecuta una sola vez con FINISHED/ERROR (deduplicado).
 */
async function subscribeToIntelligentPlanning(group_id, callback) {
	if (!group_id) {
		console.error("group_id is required");
		return;
	}

	// Si ya estábamos vigilando otro grupo, limpiamos primero.
	if (intelligentGroupId && intelligentGroupId !== group_id) {
		_stopIntelligentWatcher();
	}

	intelligentCallback = callback;
	intelligentGroupId = group_id;
	lastSeenKey = null;
	isFinalState = false;

	_ensureReconnectListener();
	_ensureWatcherListeners();

	// 1) WebSocket: feed instantáneo.
	try {
		if (!globalSocketService.isConnected()) {
			await globalSocketService.connect();
		}
		const topic = `/topic/plans/intelligent-planning/${group_id}`;
		intelligentSubscription = await globalSocketService.subscribe(
			topic,
			(data) => _onIntelligentMessage(data, "ws")
		);
	} catch (error) {
		console.error(
			`[Plans][WS:${SESSION_ID}] Error subscribing al WS de IA:`,
			error
		);
		// Aunque falle el WS, el watcher sigue funcionando con polling.
	}

	// 2) Polling worker: red de seguridad permanente.
	pollingActive = true;
	_startPollingWorker();

	// 3) Pedir permiso de notificación nativa (no fuerza prompt si ya estaba
	//    decidido). El watcher la usará para avisar si el usuario tiene la
	//    pestaña en background cuando termine.
	void browserNotificationService.requestPermission();

	// 4) Check inmediato — si la IA ya terminó (p. ej. tras recargar) lo
	//    notificamos al instante sin esperar al primer tick del worker.
	void _runStatusCheck("initial");
}

/**
 * Reanuda el polling y check REST tras encolar una nueva planificación con IA
 * sin resuscribir todo el WebSocket (sigues con el topic ya activo).
 *
 * Tras FINISHED o ERROR `_onIntelligentMessage` tiene `isFinalState=true` y
 * detiene el worker; esta función desbloquea el siguiente ciclo. La UI la
 * llama desde el modal cuando el usuario lanza trabajo de nuevo tras un
 * resultado final.
 *
 * Si el usuario llegó por `disconnect()`/`reloadPlan`, hay que llamar antes
 * a `subscribeToIntelligentPlanning` completo para volver al topic de WS.
 */
function reviveIntelligentWatchAfterQueuedJob(group_id, callback, isIterative = false) {
	if (!group_id || !callback) {
		console.warn(
			`[Plans][WS:${SESSION_ID}] reviveIntelligentWatchAfterQueuedJob falta group_id o callback`
		);
		return;
	}
	intelligentCallback = callback;
	intelligentGroupId = group_id;

	iterativePlanningMode = Boolean(isIterative);
	isFinalState = false;

	_ensureReconnectListener();
	_ensureWatcherListeners();

	pollingActive = true;
	if (pollingWorker) {
		try {
			_setPollingInterval(
				degradedMode ? FAST_POLL_INTERVAL_MS : POLL_INTERVAL_MS
			);
		} catch (_) {
			_stopPollingWorker();
			_startPollingWorker();
		}
	} else {
		_startPollingWorker();
	}

	void _runStatusCheck("enqueue-revive");
}

function unsubscribeIntelligentPlanning() {
	_stopIntelligentWatcher();
}

function _stopIntelligentWatcher() {
	if (intelligentSubscription && intelligentGroupId) {
		try {
			globalSocketService.unsubscribe(
				`/topic/plans/intelligent-planning/${intelligentGroupId}`
			);
		} catch (_) { /* noop */ }
		intelligentSubscription = null;
	}
	_cancelHeldFinished();
	intelligentCallback = null;
	intelligentGroupId = null;
	pollingActive = false;
	lastSeenKey = null;
	isFinalState = false;
	degradedMode = false;
	iterativePlanningMode = false;
	_stopPollingWorker();
	_clearWatcherListeners();
}

function _ensureWatcherListeners() {
	if (!unsubTabVisible) {
		unsubTabVisible = globalSocketService.on("tab-visible", () => {
			// Aunque el polling esté parado (FINISHED/ERROR), un check REST puede
			// descubrir un nuevo proceso o desbloquear polling vía REST.
			if (!intelligentGroupId || !intelligentCallback) return;
			void _runStatusCheck("tab-visible");
		});
	}
	if (!unsubReconnected) {
		// Ya hay un listener de reconnected en _ensureReconnectListener para
		// re-suscribir topics, pero queremos un check inmediato del estado
		// también desde aquí por si el listener anterior se registró antes.
		unsubReconnected = globalSocketService.on("reconnected", () => {
			if (!intelligentGroupId || !intelligentCallback) return;
			void _runStatusCheck("reconnected-watcher");
		});
	}
	if (!unsubMaxReconnects) {
		unsubMaxReconnects = globalSocketService.on("max-reconnects-reached", () => {
			if (!pollingActive) return;
			// El WS lleva muchos intentos fallidos. Subimos la frecuencia
			// del polling REST para que el usuario se entere antes.
			degradedMode = true;
			console.warn(
				`[Plans][WS:${SESSION_ID}] WS degradado — polling acelerado a ${FAST_POLL_INTERVAL_MS}ms`
			);
			_setPollingInterval(FAST_POLL_INTERVAL_MS);
		});
	}
}

function _clearWatcherListeners() {
	if (unsubTabVisible) { unsubTabVisible(); unsubTabVisible = null; }
	if (unsubReconnected) { unsubReconnected(); unsubReconnected = null; }
	if (unsubMaxReconnects) { unsubMaxReconnects(); unsubMaxReconnects = null; }
}

function _resumeWatcherDuringNonTerminalState(data, source) {
	const st = data && data.state;
	if (st === "FINISHED" || st === "ERROR") return;

	// Si ya estábamos en polling activo tras una corrida en curso, no tocar.
	if (!isFinalState && pollingActive && pollingWorker) return;

	const wasPinnedFinal = isFinalState;
	isFinalState = false;
	pollingActive = true;

	if (!pollingWorker) {
		_startPollingWorker();
		if (wasPinnedFinal) {
			console.log(
				`[Plans][WS:${SESSION_ID}] Polling reiniciado tras nueva corrida (${source || "?"})`
			);
		}
		return;
	}

	if (wasPinnedFinal) {
		try {
			_setPollingInterval(
				degradedMode ? FAST_POLL_INTERVAL_MS : POLL_INTERVAL_MS
			);
		} catch (_) {
			_stopPollingWorker();
			_startPollingWorker();
		}
	}
}

// ─── Hold de FINISHED de REST en modo iterativo (semana a semana) ───────────
// Cuando la semana N termina y la N+1 aún no ha comenzado, el REST polling
// puede ver FINISHED y mostrar "Ver resultados" prematuramente. Retenemos ese
// FINISHED 20 s; si llega PROCESSING antes, lo cancelamos. El WS nunca se
// retiene porque el backend solo lo manda cuando han terminado todas las semanas.

function _holdRestFinished(data, key, source) {
	if (restFinishedHoldTimer) {
		clearTimeout(restFinishedHoldTimer);
	}
	heldFinishedData = data;
	heldFinishedKey = key;
	// Paramos el polling mientras esperamos — el gap entre semanas es breve y
	// _resumeWatcherDuringNonTerminalState lo reiniciará si llega PROCESSING.
	pollingActive = false;
	_stopPollingWorker();
	console.log(
		`[Plans][WS:${SESSION_ID}] Iterative: REST FINISHED retenido ${REST_FINISHED_HOLD_MS}ms (${source})`
	);
	restFinishedHoldTimer = setTimeout(() => {
		restFinishedHoldTimer = null;
		const d = heldFinishedData;
		const k = heldFinishedKey;
		heldFinishedData = null;
		heldFinishedKey = null;
		if (!d || !intelligentCallback) return;
		if (k === lastSeenKey) return; // Ya entregado por WS mientras esperábamos
		lastSeenKey = k;
		isFinalState = true;
		pollingActive = false;
		_stopPollingWorker();
		_maybeFireBrowserNotification(d);
		console.log(
			`[Plans][WS:${SESSION_ID}] Iterative: REST FINISHED entregado tras retén de ${REST_FINISHED_HOLD_MS}ms`
		);
		try {
			intelligentCallback(d);
		} catch (e) {
			console.error(`[Plans][WS:${SESSION_ID}] Callback error (held):`, e);
		}
	}, REST_FINISHED_HOLD_MS);
}

function _cancelHeldFinished() {
	if (restFinishedHoldTimer) {
		clearTimeout(restFinishedHoldTimer);
		restFinishedHoldTimer = null;
	}
	if (heldFinishedData) {
		console.log(
			`[Plans][WS:${SESSION_ID}] Iterative: FINISHED retenido cancelado (nueva semana en curso)`
		);
	}
	heldFinishedData = null;
	heldFinishedKey = null;
}

function _onIntelligentMessage(data, source) {
	if (!data) return;
	if (!intelligentCallback) return;

	_resumeWatcherDuringNonTerminalState(data, source || "unknown");

	// Si llega PROCESSING, cancelar cualquier FINISHED retenido de la semana anterior.
	if (data.state === "PROCESSING" && restFinishedHoldTimer) {
		_cancelHeldFinished();
	}

	const key = _statusKey(data);
	if (key === lastSeenKey) {
		// Mismo estado ya entregado por otro canal. No re-notificamos.
		return;
	}

	// En modo iterativo (semana a semana), retener FINISHED que vienen del
	// REST polling. Si en REST_FINISHED_HOLD_MS llega un PROCESSING, se cancela.
	// El WS nunca se retiene (el backend solo lo manda al final de todas las semanas).
	if (
		iterativePlanningMode &&
		data.state === "FINISHED" &&
		typeof source === "string" &&
		source.startsWith("rest:")
	) {
		_holdRestFinished(data, key, source);
		return;
	}

	lastSeenKey = key;

	const final = data.state === "FINISHED" || data.state === "ERROR";
	if (final) {
		isFinalState = true;
		// Paramos el polling pero NO la suscripción WS: el usuario podría
		// volver a lanzar otra IA y queremos mantener el canal abierto.
		pollingActive = false;
		_stopPollingWorker();
		_maybeFireBrowserNotification(data);
	}

	console.log(
		`[Plans][WS:${SESSION_ID}] Intelligent state ${data.state} from ${source}`
	);
	try {
		intelligentCallback(data);
	} catch (e) {
		console.error(`[Plans][WS:${SESSION_ID}] Callback error:`, e);
	}
}

function _statusKey(status) {
	if (!status) return "null";
	const id = status.id ?? "";
	const state = status.state ?? "";
	const updated = status.updated || status.last_modified || status.end || "";
	return `${id}|${state}|${updated}`;
}

async function _runStatusCheck(reason) {
	if (!intelligentGroupId || !intelligentCallback) return;
	// No usar isFinalState aquí: puede quedar en true tras FINISHED/ERROR cuando
	// el backend ya inició un nuevo PROCESSING (p. ej. sin push por WS). Si
	// bloqueamos, el REST jamás reactiva el polling.
	if (inFlightCheck) return;

	inFlightCheck = true;
	try {
		const { plansService } = await import("./plansService");
		const response = await plansService.getPlanIntelligentStatus(
			intelligentGroupId
		);
		const status = Array.isArray(response) ? response[0] : null;
		if (!status) {
			// Sin fila devuelta ⇒ no hay planificación IA en curso pendiente para
			// este grupo. Cuando antes había cancelación/dispose, antes el código
			// dejaba el worker de polling cada ~30s activo: seguían entrando GET
			// repetidos sin sentido contra el mismo group_id hasta el disgusto del
			// backend. Paramos sólo la cadencia periódica; el topic WS sigue vivo
			// y el primer PROCESSING desde WS revivirá vía `_resumeWatcher…`, o bien
			// `reviveIntelligentWatchAfterQueuedJob` al lanzar nueva IA.
			if (pollingWorker || pollingActive) {
				pollingActive = false;
				_stopPollingWorker();
				if (reason !== "worker-tick") {
					console.debug(
						`[Plans][WS:${SESSION_ID}] Sin resultado de IA (${reason}) — polling periódico detenido (grupo ${intelligentGroupId})`
					);
				}
			}
			return;
		}
		_onIntelligentMessage(status, `rest:${reason}`);
	} catch (e) {
		console.warn(
			`[Plans][WS:${SESSION_ID}] _runStatusCheck (${reason}) falló:`,
			e
		);
	} finally {
		inFlightCheck = false;
	}
}

function _maybeFireBrowserNotification(data) {
	if (typeof document === "undefined") return;
	if (document.visibilityState === "visible") return;

	if (data.state === "FINISHED") {
		browserNotificationService.notify({
			title: "Planificación con IA finalizada",
			body: "La planificación con IA ha terminado correctamente.",
			tag: `intelligent-plan-${data.id || data.group_id || ""}`,
			onClick: () => {
				if (typeof window !== "undefined") window.focus();
			},
		});
	} else if (data.state === "ERROR") {
		browserNotificationService.notify({
			title: "Error en la planificación con IA",
			body: "La planificación con IA terminó con errores.",
			tag: `intelligent-plan-${data.id || data.group_id || ""}`,
			onClick: () => {
				if (typeof window !== "undefined") window.focus();
			},
		});
	}
}

/**
 * Compatibilidad: chequeo único del estado vía REST.
 * Útil para componentes que aún no usan el watcher.
 */
async function checkIntelligentPlanningStatus() {
	if (!currentGroupId && !intelligentGroupId) return;
	const groupId = intelligentGroupId || currentGroupId;
	try {
		const { plansService } = await import("./plansService");
		const response = await plansService.getPlanIntelligentStatus(groupId);
		const status = Array.isArray(response) ? response[0] : null;
		if (status && intelligentCallback) {
			_onIntelligentMessage(status, "manual-check");
		}
	} catch (error) {
		console.error("Error checking intelligent planning status:", error);
	}
}

// ─── Helpers de UI (sin cambios) ────────────────────────────────────────────

function _renderEmployeeList(openPlans) {
	return openPlans.map((plan, i) => (
		<li key={i}>
			<strong>{plan.employee_full_name}</strong> del{" "}
			{moment(plan.start).format("DD/MM/YY")} al{" "}
			{moment(plan.end).format("DD/MM/YY")}
		</li>
	));
}

function _checkValidDate(plan) {
	const user_full_name = currentUser.name + " " + currentUser.surname;
	const format = "YYYY-MM-DD";

	const planStart = moment(plan.start, format);
	const planEnd = moment(plan.end, format);
	const beforeTime = moment(currentStart, format);
	const afterTime = moment(currentEnd, format);

	return (
		user_full_name !== plan.employee_full_name &&
		(planStart.isBetween(beforeTime, afterTime) ||
			planStart.isSame(beforeTime) ||
			planStart.isSame(afterTime) ||
			planEnd.isBetween(beforeTime, afterTime) ||
			planEnd.isSame(beforeTime) ||
			planEnd.isSame(afterTime))
	);
}

function _filterPlans(openPlans) {
	return openPlans.filter((plan) => _checkValidDate(plan));
}

function _showMessageOutput(openPlans) {
	const filteredPlans = _filterPlans(openPlans);

	if (filteredPlans.length > 0) {
		const messageIntro =
			filteredPlans.length === 1
				? "El siguiente empleado también está viendo este plan:"
				: "Los siguientes empleados también están viendo este plan:";
		const message = (
			<div>
				<p>{messageIntro}</p>
				<ul className="lista-empleados">
					{_renderEmployeeList(filteredPlans)}
				</ul>
			</div>
		);

		_showPlainInfoToast(message, {
			autoClose: 5000,
			toastId: "userAlert",
		});
	} else {
		toast.dismiss("userAlert");
	}
}

function _showMessageEdited(openPlan) {
	const validDate = _checkValidDate(openPlan);

	if (validDate) {
		const message =
			openPlan.employee_full_name +
			" ha actualizado la planificación entre las fechas " +
			moment(openPlan.start).format("DD/MM/YY") +
			" y " +
			moment(openPlan.end).format("DD/MM/YY") +
			". Refresca la página para ver los cambios.";

		_showPlainInfoToast(message, {
			autoClose: 15000,
			toastId: "editAlert",
		});
	}
}

function _showPlainInfoToast(message, options = {}) {
	toast(
		() => (
			<div className="plain-toastify-message">
				<div className="plain-toastify-message__icon">!</div>
				<div className="plain-toastify-message__text">{message}</div>
			</div>
		),
		{
			position: "top-right",
			hideProgressBar: true,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			theme: "dark",
			...options,
		}
	);
}
